import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  Upload,
  CheckCircle,
  Clock,
  AlertCircle,
  TrendingUp,
  History,
  Copy,
  X,
  Loader,
} from "lucide-react";
import { cocbGetStats, cocbExtractDocument, cocbGetWorkOrders } from "../api";
import "./COCBDashboard.css";

const COCBDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentWorkOrders, setRecentWorkOrders] = useState([]);
  
  // Extract state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uid, setUid] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [extractionResult, setExtractionResult] = useState(null);
  const [extractionError, setExtractionError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsData, workOrdersData] = await Promise.all([
        cocbGetStats().catch(() => null),
        cocbGetWorkOrders({ limit: 5 }).catch(() => ({ work_orders: [] }))
      ]);
      
      if (statsData) {
        setStats(statsData);
      }
      setRecentWorkOrders(workOrdersData?.work_orders || []);
    } catch (err) {
      console.error("Failed to fetch COCB data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setExtractionResult(null);
      setExtractionError(null);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      setSelectedFile(file);
      setExtractionResult(null);
      setExtractionError(null);
    }
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleExtract = async () => {
    if (!selectedFile) return;

    setExtracting(true);
    setExtractionError(null);
    setExtractionResult(null);

    try {
      const result = await cocbExtractDocument(selectedFile, uid || undefined);
      setExtractionResult(result);
      // Refresh data after extraction
      fetchData();
    } catch (err) {
      console.error("Extraction failed:", err);
      setExtractionError(err.message || "Extraction failed");
    } finally {
      setExtracting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const clearResult = () => {
    setExtractionResult(null);
    setExtractionError(null);
    setSelectedFile(null);
    setUid("");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed": return "#10b981";
      case "processing": return "#3b82f6";
      case "failed": return "#ef4444";
      default: return "#6b7280";
    }
  };

  return (
    <div className="cocb-dashboard">
      {/* Welcome Banner */}
      <div className="cocb-welcome-banner">
        <div className="cocb-welcome-content">
          <h1>COCB Document Extraction</h1>
          <p>Extract Change of Distributor form data using AI-powered document processing</p>
        </div>
        <div className="cocb-welcome-actions">
          <button className="cocb-welcome-btn primary" onClick={() => navigate("/v1/cocb/history")}>
            <History size={20} />
            View All Work Orders
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="cocb-stats-overview">
        <div className="cocb-stat-card total">
          <div className="cocb-stat-icon">
            <FileText size={24} />
          </div>
          <div className="cocb-stat-info">
            <span className="cocb-stat-value">{loading ? "-" : stats?.total_work_orders || 0}</span>
            <span className="cocb-stat-label">Total Extractions</span>
          </div>
        </div>

        <div className="cocb-stat-card completed">
          <div className="cocb-stat-icon">
            <CheckCircle size={24} />
          </div>
          <div className="cocb-stat-info">
            <span className="cocb-stat-value">{loading ? "-" : stats?.by_status?.completed || 0}</span>
            <span className="cocb-stat-label">Completed</span>
          </div>
        </div>

        <div className="cocb-stat-card processing">
          <div className="cocb-stat-icon">
            <Clock size={24} />
          </div>
          <div className="cocb-stat-info">
            <span className="cocb-stat-value">{loading ? "-" : stats?.by_status?.processing || 0}</span>
            <span className="cocb-stat-label">Processing</span>
          </div>
        </div>

        <div className="cocb-stat-card failed">
          <div className="cocb-stat-icon">
            <AlertCircle size={24} />
          </div>
          <div className="cocb-stat-info">
            <span className="cocb-stat-value">{loading ? "-" : stats?.by_status?.failed || 0}</span>
            <span className="cocb-stat-label">Failed</span>
          </div>
        </div>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="cocb-main-grid">
        {/* Left: Upload & Extract */}
        <div className="cocb-card">
          <div className="cocb-card-header">
            <Upload size={20} />
            <span>Extract Document</span>
          </div>
          <div className="cocb-card-body">
            {/* File Upload Zone */}
            <div 
              className={`cocb-upload-zone ${selectedFile ? 'has-file' : ''}`}
              onClick={() => document.getElementById('cocb-file-input').click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              {selectedFile ? (
                <>
                  <FileText size={32} className="cocb-file-icon" />
                  <p className="cocb-file-name">{selectedFile.name}</p>
                  <p className="cocb-file-size">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                </>
              ) : (
                <>
                  <Upload size={32} />
                  <p>Click or drag file here to upload</p>
                  <p className="cocb-upload-hint">PDF, TIFF, JPG, PNG (max 50MB)</p>
                </>
              )}
            </div>
            <input
              type="file"
              id="cocb-file-input"
              accept=".pdf,.tiff,.tif,.jpg,.jpeg,.png"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            {/* UID Input */}
            <div className="cocb-form-group">
              <label htmlFor="cocb-uid">UID (optional tracking ID)</label>
              <input
                type="text"
                id="cocb-uid"
                value={uid}
                onChange={(e) => setUid(e.target.value)}
                placeholder="Optional unique identifier"
              />
            </div>

            {/* Extract Button */}
            <button
              className="cocb-extract-btn"
              onClick={handleExtract}
              disabled={!selectedFile || extracting}
            >
              {extracting ? (
                <>
                  <Loader size={18} className="cocb-spinner" />
                  Extracting...
                </>
              ) : (
                <>
                  <Upload size={18} />
                  Extract Document
                </>
              )}
            </button>

            {/* Error Display */}
            {extractionError && (
              <div className="cocb-error-message">
                <AlertCircle size={16} />
                <span>{extractionError}</span>
              </div>
            )}
          </div>
        </div>

        {/* Right: Results */}
        <div className="cocb-card">
          <div className="cocb-card-header">
            <TrendingUp size={20} />
            <span>Extraction Results</span>
            {extractionResult && (
              <button className="cocb-clear-btn" onClick={clearResult}>
                <X size={16} />
              </button>
            )}
          </div>
          <div className="cocb-card-body">
            {extractionResult ? (
              <div className="cocb-result-section">
                {/* Result Meta */}
                <div className="cocb-result-meta">
                  <div className="cocb-meta-item">
                    <span>Status:</span>
                    <span className={`cocb-status-badge ${extractionResult.meta_data?.status || 'completed'}`}>
                      {extractionResult.meta_data?.status || 'completed'}
                    </span>
                  </div>
                  <div className="cocb-meta-item">
                    <span>File:</span>
                    <strong>{extractionResult.meta_data?.filename || selectedFile?.name}</strong>
                  </div>
                  <div className="cocb-meta-item">
                    <span>Model:</span>
                    <strong>{extractionResult.meta_data?.model || '-'}</strong>
                  </div>
                  <div className="cocb-meta-item">
                    <span>Time:</span>
                    <strong>{extractionResult.meta_data?.processing_time || '-'}</strong>
                  </div>
                  <div className="cocb-meta-item">
                    <span>Fields:</span>
                    <strong>{extractionResult.meta_data?.field_count || Object.keys(extractionResult.extracted_data || {}).length}</strong>
                  </div>
                </div>

                {/* Result JSON */}
                <div className="cocb-result-json-header">
                  <strong>Extracted Data</strong>
                  <button
                    className="cocb-copy-btn"
                    onClick={() => copyToClipboard(JSON.stringify(extractionResult.extracted_data, null, 2))}
                  >
                    <Copy size={14} />
                    Copy JSON
                  </button>
                </div>
                <pre className="cocb-result-json">
                  {JSON.stringify(extractionResult.extracted_data, null, 2)}
                </pre>
              </div>
            ) : (
              <div className="cocb-no-results">
                <FileText size={48} />
                <p>Upload a document and click Extract to see results</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Work Orders */}
      <div className="cocb-recent-section">
        <h2 className="cocb-section-title">
          <History size={20} />
          Recent Extractions
        </h2>
        
        {recentWorkOrders.length > 0 ? (
          <div className="cocb-recent-list">
            {recentWorkOrders.map((wo) => (
              <div
                key={wo._id || wo.work_order_id}
                className="cocb-recent-item"
                onClick={() => navigate(`/v1/cocb/work-order/${wo._id || wo.work_order_id}`)}
              >
                <div className="cocb-recent-icon">
                  <FileText size={20} />
                </div>
                <div className="cocb-recent-info">
                  <div className="cocb-recent-filename">{wo.filename || wo.uid || 'Document'}</div>
                  <div className="cocb-recent-meta">
                    {wo.created_at && new Date(wo.created_at).toLocaleString()}
                  </div>
                </div>
                <span
                  className="cocb-recent-status"
                  style={{ backgroundColor: `${getStatusColor(wo.status)}20`, color: getStatusColor(wo.status) }}
                >
                  {wo.status || 'pending'}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="cocb-empty-recent">
            <p>No recent extractions. Upload a document to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default COCBDashboard;
